import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { VERSION } from "../../version";
import { usePermissions } from "../../contexts/PermissionContext";
import { ViewGuard } from "../auth/PermissionGuards";

interface SidebarLinkProps {
  href: string;
  icon: string;
  label: string;
  isActive: boolean;
  collapsed?: boolean;
}

const SidebarLink = ({
  href,
  icon,
  label,
  isActive,
  collapsed = false,
}: SidebarLinkProps) => {
  if (collapsed) {
    return (
      <li className="mb-1">
        <TooltipProvider>
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <Link href={href}>
                <div
                  className={cn(
                    "flex items-center justify-center p-2 rounded cursor-pointer",
                    isActive ? "bg-[#2B88D8]" : "hover:bg-[#2B88D8]/70",
                  )}
                >
                  <span className="material-icons text-sm">{icon}</span>
                </div>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{label}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </li>
    );
  }

  return (
    <li className="mb-1">
      <Link href={href}>
        <div
          className={cn(
            "flex items-center px-2 py-2 rounded cursor-pointer",
            isActive ? "bg-[#2B88D8]" : "hover:bg-[#2B88D8]/70",
          )}
        >
          <span className="material-icons mr-3 text-sm">{icon}</span>
          {label}
        </div>
      </Link>
    </li>
  );
};

interface SidebarSectionProps {
  title: string;
  children: React.ReactNode;
  collapsed?: boolean;
  defaultExpanded?: boolean;
}

const SidebarSection = ({
  title,
  children,
  collapsed = false,
  defaultExpanded = true,
}: SidebarSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  if (collapsed) {
    return (
      <div className="mb-2">
        <div className="text-xs uppercase font-semibold px-2 py-1 text-white/60">
          {title.substring(0, 1)}
        </div>
        <ul>{children}</ul>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <button
        className="w-full flex items-center justify-between text-xs uppercase font-semibold px-2 py-1 text-white/80 hover:text-white"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {title}
        {isExpanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
      </button>
      {isExpanded && <ul className="mt-1">{children}</ul>}
    </div>
  );
};

export interface SidebarProps {
  collapsed?: boolean;
}

const Sidebar = ({ collapsed = false }: SidebarProps) => {
  const [location] = useLocation();
  usePermissions();

  return (
    <div
      className={cn(
        "bg-[#0078D4] text-white flex flex-col transition-all duration-300 ease-in-out h-screen flex-shrink-0",
        collapsed ? "w-16" : "w-64",
      )}
    >
      <div
        className={cn(
          "py-3 border-b border-[#2B88D8] flex",
          collapsed ? "px-2 justify-center" : "px-4 items-center",
        )}
      >
        <div className="bg-white p-1 rounded-md flex items-center justify-center">
          <img
            src="/images/peter-lang-logo.png"
            alt="Peter Lang Logo"
            style={{ width: "100px" }}
            className="object-contain"
          />
        </div>

        {!collapsed && (
          <h1 className="text-xl font-semibold ml-3">Publishing Nexus</h1>
        )}
      </div>
      <nav
        className={cn(
          "flex-1 py-4 overflow-y-auto",
          collapsed ? "px-2" : "px-4",
        )}
      >
        {/* Direct Links - No Collapsible Sections */}
        <ul className="mb-4">
          <ViewGuard section="fieldMappings" showFallback={false}>
            <SidebarLink
              href="/fields/mappings"
              icon="sync_alt"
              label="Fields"
              isActive={location.startsWith("/fields")}
              collapsed={collapsed}
            />
          </ViewGuard>
          <SidebarLink
            href="/dashboard"
            icon="dashboard"
            label="Dashboard"
            isActive={location.startsWith("/dashboard")}
            collapsed={collapsed}
          />
          <SidebarLink
            href="/sql-sync"
            icon="sync"
            label="SQL Sync"
            isActive={location.startsWith("/sql-sync")}
            collapsed={collapsed}
          />
          <SidebarLink
            href="/op-sync"
            icon="published_with_changes"
            label="OP Sync"
            isActive={location.startsWith("/op-sync")}
            collapsed={collapsed}
          />
          <SidebarLink
            href="/op-readiness"
            icon="fact_check"
            label="OP Readiness"
            isActive={location.startsWith("/op-readiness")}
            collapsed={collapsed}
          />
          <SidebarLink
            href="/sharepoint/dashboard"
            icon="cloud_upload"
            label="SharePoint"
            isActive={location.startsWith("/sharepoint")}
            collapsed={collapsed}
          />
          <ViewGuard section="royaltyStatements" showFallback={false}>
            <SidebarLink
              href="/royalty-statements"
              icon="receipt_long"
              label="Royalty Statements"
              isActive={location === "/royalty-statements"}
              collapsed={collapsed}
            />
            <SidebarLink
              href="/royalty-overdue"
              icon="warning"
              label="Overdue Royalties"
              isActive={location === "/royalty-overdue"}
              collapsed={collapsed}
            />
          </ViewGuard>
          <SidebarLink
            href="/title-folders/dashboard"
            icon="folder_special"
            label="Projects"
            isActive={location.startsWith("/title-folders")}
            collapsed={collapsed}
          />
          <SidebarLink
            href="/merge"
            icon="merge_type"
            label="Record Merge"
            isActive={location === "/merge"}
            collapsed={collapsed}
          />
          <SidebarLink
            href="/copyright-page"
            icon="description"
            label="Copyright Page"
            isActive={location.startsWith("/copyright-page")}
            collapsed={collapsed}
          />
          <SidebarLink
            href="/prelim"
            icon="auto_stories"
            label="Prelim Generator"
            isActive={location === "/prelim"}
            collapsed={collapsed}
          />
          <SidebarLink
            href="/sentiment/dashboard"
            icon="psychology"
            label="Sentiment"
            isActive={location.startsWith("/sentiment")}
            collapsed={collapsed}
          />
        </ul>

        {/* System Section */}
        <SidebarSection
          title="System"
          collapsed={collapsed}
          defaultExpanded={true}
        >
          <SidebarLink
            href="/scheduled-tasks"
            icon="schedule"
            label="Scheduler"
            isActive={location.startsWith("/scheduled-tasks") || location === "/task-executions"}
            collapsed={collapsed}
          />
          <SidebarLink
            href="/monitoring/activity-log"
            icon="speed"
            label="Monitoring"
            isActive={location.startsWith("/monitoring")}
            collapsed={collapsed}
          />
          <SidebarLink
            href="/adf/watermark-config"
            icon="edit_note"
            label="Watermark Config"
            isActive={location === "/adf/watermark-config"}
            collapsed={collapsed}
          />
          <ViewGuard section="userManagement" showFallback={false}>
            <SidebarLink
              href="/security/users"
              icon="security"
              label="Security"
              isActive={location.startsWith("/security")}
              collapsed={collapsed}
            />
          </ViewGuard>
        </SidebarSection>
      </nav>
      <div
        className={cn(
          "text-xs text-white/60 px-4 pb-2 mt-auto",
          collapsed ? "text-center px-2" : "",
        )}
        style={{ letterSpacing: "0.03em" }}
      >
        v: {VERSION}
      </div>
    </div>
  );
};

export default Sidebar;
